import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { SERVICES, COMPANY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs tracking-[0.2em] uppercase text-mh-slate">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full mt-2 bg-transparent border-b border-mh-line py-3 text-white placeholder:text-mh-muted focus:border-mh-orange focus:outline-none transition-colors";

export const QuoteForm = ({ innerRef }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Merci de renseigner votre nom, email et téléphone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setDone(true);
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
      setForm({ name: "", email: "", phone: "", city: "", service: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Appelez-nous au " + COMPANY.phoneDisplay);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" ref={innerRef} className="bg-mh-bg2 border-y border-mh-line scroll-mt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-[0.9fr_1.1fr] gap-16">
        <div>
          <span className="text-xs tracking-[0.25em] uppercase text-mh-orange font-bold">Devis gratuit</span>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight text-white leading-[0.9]">
            Parlez-nous de votre projet
          </h2>
          <p className="mt-6 text-mh-slate leading-relaxed max-w-md">
            Décrivez votre besoin, nous revenons vers vous avec un devis clair et sans engagement.
            Réponse rapide, déplacement et estimation offerts.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <a href={`tel:${COMPANY.phone}`} className="block text-white hover:text-mh-orange transition-colors" data-testid="quote-phone">
              📞 {COMPANY.phoneDisplay}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="block text-white hover:text-mh-orange transition-colors" data-testid="quote-email">
              ✉️ {COMPANY.email}
            </a>
            <p className="text-mh-slate">{COMPANY.address}, {COMPANY.zip}</p>
          </div>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="border border-mh-orange bg-mh-bg p-12 flex flex-col justify-center"
            data-testid="quote-success"
          >
            <h3 className="font-display font-bold text-3xl text-white">Merci !</h3>
            <p className="mt-4 text-mh-slate">Votre demande a bien été enregistrée. Notre équipe vous recontacte dans les plus brefs délais.</p>
            <button onClick={() => setDone(false)} className="mt-8 text-mh-orange font-semibold text-sm self-start" data-testid="quote-new">
              Envoyer une autre demande
            </button>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-6" data-testid="quote-form">
            <Field label="Nom complet *">
              <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Jean Dupont" data-testid="quote-input-name" />
            </Field>
            <Field label="Téléphone *">
              <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" data-testid="quote-input-phone" />
            </Field>
            <Field label="Email *">
              <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jean@email.fr" data-testid="quote-input-email" />
            </Field>
            <Field label="Ville">
              <input className={inputCls} value={form.city} onChange={set("city")} placeholder="Fréjus" data-testid="quote-input-city" />
            </Field>
            <Field label="Prestation">
              <select className={`${inputCls} appearance-none`} value={form.service} onChange={set("service")} data-testid="quote-input-service">
                <option value="" className="bg-mh-bg2">Sélectionnez…</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title} className="bg-mh-bg2">{s.title}</option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Votre message">
                <textarea rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Décrivez votre projet…" data-testid="quote-input-message" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-mh-orange hover:bg-mh-orangeHover disabled:opacity-60 text-white font-semibold px-8 py-4 transition-colors"
                data-testid="quote-submit"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
                {loading ? "Envoi…" : "Envoyer ma demande"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
